import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const SSO_LOGIN_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/socialpulse/login`;

const SSO_ERRORS = {
  not_configured: "SocialPulse sign-in is being switched on — please use email for now.",
  denied: "The SocialPulse sign-in was cancelled or denied.",
  state: "That sign-in attempt expired. Please try again.",
  exchange: "We couldn't complete the SocialPulse sign-in. Please try again.",
  no_email: "Your SocialPulse account didn't share an email address.",
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ssoError = searchParams.get("sso_error");
    if (ssoError) {
      setError(SSO_ERRORS[ssoError] || "SocialPulse sign-in failed. Please try again.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back.");
      nav(location.state?.from || "/app", { replace: true });
    } else {
      setError(res.error);
    }
  }

  return (
    <section className="min-h-[80vh] grid lg:grid-cols-2">
      {/* Editorial panel */}
      <div className="hidden lg:flex relative border-r border-[color:var(--ink-line)] bg-[color:var(--bone-2)] p-14 flex-col justify-between">
        <div>
          <div className="overline mb-6">Volume I · The Living Record</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-[0.95] tracking-tight">
            Return to the archive.
          </h2>
          <p className="mt-6 font-italic-serif italic text-lg text-[color:var(--ink-soft)] max-w-md">
            "Every family is a library. We are only the librarians for a while."
          </p>
        </div>
        <div className="numeric-index text-[color:var(--ink-soft)]">
          I / II · Sign in · Est. 2026
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-8 lg:p-14">
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
          data-testid="login-form"
        >
          <div className="overline mb-3">Chapter I</div>
          <h1 className="font-serif-display text-4xl tracking-tight mb-2">Sign in</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mb-8">
            Continue writing your family's chronicle.
          </p>

          <label className="block mb-4">
            <span className="overline">Email</span>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-3 text-lg focus:outline-none focus:border-[color:var(--rust)]"
              autoComplete="email"
            />
          </label>

          <label className="block mb-6">
            <span className="overline">Password</span>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-3 text-lg focus:outline-none focus:border-[color:var(--rust)]"
              autoComplete="current-password"
            />
          </label>

          {error && (
            <div
              data-testid="login-error"
              className="mb-4 text-sm text-[color:var(--rust)] border-l-2 border-[color:var(--rust)] pl-3 py-1"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="btn-rust w-full justify-center disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-[color:var(--ink-soft)]">
            <div className="rule flex-1" />
            <span className="overline">or</span>
            <div className="rule flex-1" />
          </div>

          <a
            href={SSO_LOGIN_URL}
            data-testid="sso-socialpulse"
            className="btn-ghost w-full justify-center"
          >
            Continue with SocialPulse
          </a>

          <p className="mt-8 text-sm text-[color:var(--ink-soft)]">
            New to the archive?{" "}
            <Link
              to="/app/register"
              data-testid="login-goto-register"
              className="link-underline text-[color:var(--ink)]"
            >
              Create your family archive
            </Link>
          </p>
        </motion.form>
      </div>
    </section>
  );
}
